import styled, { css } from "styled-components/native";
import { useQaLabel, useQaLabelActive } from "../../../../hooks";
import { space } from "../../../../theming/styledSystem";
import { StyledButtonProps, ButtonContainerProps } from "./types";
import { Theme } from "../../../../theming/types";

export const ButtonContainer = styled.View<ButtonContainerProps>`
  ${space};
  position: relative;
  z-index: ${({ isActive, isHovering }: ButtonContainerProps) =>
    isActive || isHovering ? "1" : "0"};
  flex-direction: row;
  ${({ isFullWidth }: ButtonContainerProps) =>
    isFullWidth &&
    css`
      flex-grow: 1;
      flex-basis: 0;
      flex-shrink: 0;
    `}
`;

export const StyledButton = styled.TouchableHighlight.attrs<{
  qaLabel: string;
  isActive: boolean;
  theme: Theme;
}>((props: { qaLabel: string; isActive: boolean; theme: Theme }) => ({
  accessibilityRole: "button",
  underlayColor:
    props.theme.colorTokens.component.button.secondary.background.hover,
  ...useQaLabelActive(props.qaLabel, props.isActive ? "selected" : "")
}))<StyledButtonProps>`
  ${space};
  align-self: flex-start;
  flex-grow: 0;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 ${({ theme }: { theme: Theme }) => theme.space["space-5"]};
  ${({ hasBorderRadius, theme }: { hasBorderRadius?: boolean; theme: Theme }) =>
    hasBorderRadius &&
    css`
      border-radius: ${theme.radii.xs};
    `}
  ${({ hasShadow, theme }: { hasShadow?: boolean; theme: Theme }) =>
    hasShadow &&
    css`
      box-shadow: ${theme.elevations.low};
    `}
  ${({ isFullWidth }: { isFullWidth?: boolean }) =>
    isFullWidth &&
    css`
      flex-grow: 1;
      flex-basis: 0;
      flex-shrink: 0;
      padding: 0;
    `}
  ${({ isHovering, theme }: { isHovering: boolean; theme: Theme }) =>
    isHovering &&
    css`
      background-color: ${theme.colorTokens.component.button.buttonLink
        .background.hover};
    `}
  ${({ isActive, theme }: { isActive: boolean; theme: Theme }) =>
    isActive &&
    css`
      background-color: ${theme.colorTokens.component.button.secondary
        .background.hover};
    `}
  ${({
    isActive,
    isHovering,
    theme
  }: {
    isActive: boolean;
    isHovering: boolean;
    theme: Theme;
  }) =>
    isActive &&
    isHovering &&
    css`
      background-color: ${theme.colorTokens.component.button.secondary
        .background.base};
    `}
`;

export const ButtonText = styled.Text.attrs<{ qaLabel: string }>(
  (props: { qaLabel: string }) => ({
    ...useQaLabel(props.qaLabel)
  })
)<{
  isActive: boolean;
  isHovering: boolean;
  qaLabel: string;
  color: string;
  theme: Theme;
  children?: React.ReactNode;
}>`
  font-family: ${({ theme }: { theme: Theme }) => theme.fonts.medium};
  font-size: ${({ theme }: { theme: Theme }) => theme.fontSizes.s};
  font-weight: ${({ theme }: { theme: Theme }) => theme.fontWeights.medium};
  color: ${({ theme, color }: { theme: Theme; color: string }) =>
    color || theme.colorTokens.component.button.tertiary.content.base};
`;

export const ButtonRing = styled.View<{
  isLastChild?: boolean;
  isFirstChild?: boolean;
  isActive: boolean;
  isHovering: boolean;
  theme: Theme;
}>`
  position: absolute;
  top: 0;
  right: ${({ isLastChild }: { isLastChild?: boolean }) =>
    isLastChild ? "0" : "-1px"};
  bottom: 0;
  left: 0;
  border: 1px solid
    ${({ theme }: { theme: Theme }) =>
      theme.colorTokens.component.button.tertiary.border.base};
  ${({ isFirstChild, theme }: { isFirstChild?: boolean; theme: Theme }) =>
    isFirstChild &&
    css`
      border-bottom-left-radius: ${theme.radii.xs};
      border-top-left-radius: ${theme.radii.xs};
    `}
  ${({ isLastChild, theme }: { isLastChild?: boolean; theme: Theme }) =>
    isLastChild &&
    css`
      border-bottom-right-radius: ${theme.radii.xs};
      border-top-right-radius: ${theme.radii.xs};
    `}
  ${({ isHovering, theme }: { isHovering: boolean; theme: Theme }) =>
    isHovering &&
    css`
      border: 1px solid
        ${theme.colorTokens.component.button.tertiary.border.hover};
    `}
  ${({ isActive, theme }: { isActive: boolean; theme: Theme }) =>
    isActive &&
    css`
      border: 1px solid
        ${theme.colorTokens.component.button.tertiary.border.active};
    `}
`;

export const TextContainer = styled.View<{ children?: React.ReactNode }>`
  flex-direction: row;
  align-items: center;
`;
